"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { Campaign, getActiveCampaigns, trackCampaignEvent } from "@/services/campaign";
import { getClientRegion, isValidRegion } from "@/services/region";
import { useIsMobile } from "./useIsMobile";

// framer-motion only loads once a campaign is actually active (see below) —
// most page loads have none, so this keeps it out of the root bundle.
const CampaignAnimatedRenderer = dynamic(() => import("./CampaignAnimatedRenderer"), {
  ssr: false,
});

// The scope every existing overlay/chrome campaign (modal/top_bar/bottom_bar/
// floating) belongs to — unchanged behavior, pre-dating Zones entirely. Every
// `banner` campaign instead belongs to the scope named after its own `zone`.
const GLOBAL_SCOPE = "global";

interface ScopeState {
  /** All campaigns in this scope sharing the current highest priority — rendered simultaneously. */
  active: Campaign[];
  /** Still-queued lower-priority groups for this scope, in priority-desc order. */
  remaining: Campaign[][];
}

interface CampaignContextType {
  /**
   * All campaigns sharing the current highest priority among eligible
   * campaigns in the global (non-zoned) scope — rendered simultaneously.
   * Never a mix of priorities. Unchanged by the introduction of Zones.
   */
  activeCampaigns: Campaign[];
  /**
   * The current active (top-priority) group for every zone that has
   * eligible banner campaigns, keyed by zone name. Each zone's queue is
   * computed and advanced completely independently of the global scope and
   * of every other zone.
   */
  zoneCampaigns: Record<string, Campaign[]>;
  /** `scope` defaults to the global scope — existing callers are unaffected. */
  closeCampaign: (campaignId: number, scope?: string) => void;
  trackClick: (campaignId: number) => void;
  sessionId: string;
}

/**
 * Partition campaigns (already priority-sorted desc by the API) into groups
 * of equal priority, preserving priority-desc order across groups. Two
 * campaigns with the same priority always land in the same group.
 */
function groupByPriority(campaigns: Campaign[]): Campaign[][] {
  const sorted = [...campaigns].sort((a, b) => b.priority - a.priority);
  const groups: Campaign[][] = [];

  for (const campaign of sorted) {
    const currentGroup = groups[groups.length - 1];
    if (currentGroup && currentGroup[0].priority === campaign.priority) {
      currentGroup.push(campaign);
    } else {
      groups.push([campaign]);
    }
  }

  return groups;
}

/**
 * Which independent queue a campaign belongs to: every existing type shares
 * the single global scope (unchanged), a `banner` belongs to the scope named
 * after its own zone. A `banner` with no/invalid zone can't be mounted
 * anywhere — dropped rather than guessed at, so stale data fails safely
 * instead of rendering nowhere meaningful or crashing.
 */
function scopeKeyFor(campaign: Campaign): string | null {
  if (campaign.type !== "banner") {
    return GLOBAL_SCOPE;
  }
  return campaign.zone ?? null;
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

export const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error("useCampaign must be used within a CampaignProvider");
  }
  return context;
};

// Normalize pathname to strip region and locale prefixes for accurate wildcard matching
function getNormalizedPagePath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);

  // Strip region code if present (e.g. eg, sa, ae)
  const first = segments[0];
  if (first && isValidRegion(first)) {
    segments.shift();
  }

  // Strip locale code if present (e.g. en, ar)
  const locales = ["en", "ar"];
  const second = segments[0];
  if (second && locales.includes(second)) {
    segments.shift();
  } else if (first && locales.includes(first) && !isValidRegion(first)) {
    segments.shift();
  }

  return "/" + segments.join("/");
}

export const CampaignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Every scope's queue (global + one per zone with eligible banners), keyed
  // by scopeKeyFor(). Replaced wholesale on every fetch — a scope absent
  // from the latest fetch (e.g. the previous route's zone) simply vanishes,
  // so stale campaigns never linger across navigation.
  const [scopes, setScopes] = useState<Record<string, ScopeState>>({});
  // Once true, stays true: keeps CampaignAnimatedRenderer (and framer-motion)
  // mounted for the rest of the session so AnimatePresence can still animate
  // later transitions (closing, a group advancing to the next one).
  const [hasHadActiveCampaign, setHasHadActiveCampaign] = useState(false);
  const pathname = usePathname();
  const locale = useLocale();
  const isMobile = useIsMobile();
  // Keyed by campaign id, not by scope — a given campaign id only ever
  // belongs to one scope at a time, so one shared map is sufficient and
  // mirrors the pre-Zone implementation.
  const timersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  // Tracks which campaign ids already had a 'view' recorded for the current
  // page fetch, so removing one campaign from a multi-campaign group doesn't
  // re-fire 'view' tracking for the campaigns still showing.
  const viewedCampaignIdsRef = useRef<Set<number>>(new Set());
  const [sessionId, setSessionId] = useState<string>("");

  const activeCampaigns = scopes[GLOBAL_SCOPE]?.active ?? [];

  const zoneCampaigns = useMemo(() => {
    const result: Record<string, Campaign[]> = {};
    for (const [scope, state] of Object.entries(scopes)) {
      if (scope !== GLOBAL_SCOPE) {
        result[scope] = state.active;
      }
    }
    return result;
  }, [scopes]);

  useEffect(() => {
    if (activeCampaigns.length > 0) {
      setHasHadActiveCampaign(true);
    }
  }, [activeCampaigns]);

  // Initialize unique session ID
  useEffect(() => {
    let sessId = sessionStorage.getItem("campaign_session_id");
    if (!sessId) {
      sessId = "sess_" + Math.random().toString(36).substring(2) + Date.now().toString(36);
      sessionStorage.setItem("campaign_session_id", sessId);
    }
    setSessionId(sessId);
  }, []);

  // Check frequency rules locally
  const checkFrequency = useCallback((campaign: Campaign): boolean => {
    const { id, frequency } = campaign;

    if (frequency === "always") {
      return true;
    }

    if (frequency === "once_session") {
      return sessionStorage.getItem(`campaign_viewed_${id}`) !== "true";
    }

    if (frequency === "once_day") {
      const lastShown = localStorage.getItem(`campaign_viewed_day_${id}`);
      if (lastShown) {
        const timeDiff = Date.now() - parseInt(lastShown, 10);
        return timeDiff > 24 * 60 * 60 * 1000; // 24 hours
      }
      return true;
    }

    if (frequency === "once_user") {
      // Authenticated once_user is handled by the backend.
      // For guests, fallback to permanent localStorage view restriction.
      const lastShown = localStorage.getItem(`campaign_viewed_user_guest_${id}`);
      return lastShown !== "true";
    }

    return true;
  }, []);

  // Fetch campaigns for the current page
  useEffect(() => {
    if (!pathname || !sessionId) return;

    const fetchCampaigns = async () => {
      try {
        const normalizedPage = getNormalizedPagePath(pathname);
        const regionSlug = getClientRegion(pathname) || undefined;
        const device = isMobile ? "mobile" : "desktop";

        const campaigns = await getActiveCampaigns(normalizedPage, regionSlug, locale, device);

        // Filter campaigns by local frequency rules
        const eligible = campaigns.filter(checkFrequency);

        // Partition into independent scopes: the single global scope for
        // every existing type, plus one scope per zone with eligible
        // banners. A banner with no/invalid zone is dropped — it has
        // nowhere safe to render.
        const buckets: Record<string, Campaign[]> = {};
        for (const campaign of eligible) {
          const scope = scopeKeyFor(campaign);
          if (!scope) continue;
          (buckets[scope] ??= []).push(campaign);
        }

        // Group by priority (desc) independently within each scope: all
        // campaigns sharing the highest priority in that scope become its
        // active group and render together; lower priority groups wait in
        // that scope's own queue until its active group closes. A scope's
        // queue never affects any other scope's.
        const nextScopes: Record<string, ScopeState> = {};
        for (const [scope, campaignsInScope] of Object.entries(buckets)) {
          const groups = groupByPriority(campaignsInScope);
          nextScopes[scope] = { active: groups[0] ?? [], remaining: groups.slice(1) };
        }

        // A fresh fetch (new page) resets view tracking for this batch.
        viewedCampaignIdsRef.current = new Set();
        setScopes(nextScopes);
      } catch (err) {
        console.error("Failed to load campaigns:", err);
      }
    };

    fetchCampaigns();
  }, [pathname, sessionId, locale, isMobile, checkFrequency]);

  // Track impression (view) for every campaign newly entering any scope's
  // active group, and arm each one's own independent auto-close timer.
  // Deliberately does not tear down/recreate timers for campaigns that
  // remain active across a re-run (e.g. a sibling in the group being
  // closed) — only for campaigns that actually left every active set.
  useEffect(() => {
    if (!sessionId) return;

    const allActive = Object.entries(scopes).flatMap(([scope, state]) =>
      state.active.map(campaign => ({ campaign, scope }))
    );
    if (allActive.length === 0) return;

    const timers = timersRef.current;
    const activeIds = new Set(allActive.map(({ campaign }) => campaign.id));

    timers.forEach((timer, id) => {
      if (!activeIds.has(id)) {
        clearTimeout(timer);
        timers.delete(id);
      }
    });

    allActive.forEach(({ campaign, scope }) => {
      if (!viewedCampaignIdsRef.current.has(campaign.id)) {
        trackCampaignEvent(campaign.id, "view", sessionId).catch(err => {
          console.error("Failed to track campaign view event:", err);
        });

        // Record locally based on frequency
        const { id, frequency } = campaign;
        if (frequency === "once_session") {
          sessionStorage.setItem(`campaign_viewed_${id}`, "true");
        } else if (frequency === "once_day") {
          localStorage.setItem(`campaign_viewed_day_${id}`, Date.now().toString());
        } else if (frequency === "once_user") {
          localStorage.setItem(`campaign_viewed_user_guest_${id}`, "true");
        }

        viewedCampaignIdsRef.current.add(campaign.id);
      }

      // Set an auto close timer if applicable and not already running.
      if (
        !timers.has(campaign.id) &&
        campaign.display_seconds &&
        (campaign.close_type === "auto" || campaign.close_type === "both")
      ) {
        timers.set(
          campaign.id,
          setTimeout(() => {
            closeCampaign(campaign.id, scope);
          }, campaign.display_seconds * 1000)
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopes, sessionId]);

  // Clear all outstanding timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const closeCampaign = useCallback(
    (campaignId: number, scope: string = GLOBAL_SCOPE) => {
      // Track close event in DB
      trackCampaignEvent(campaignId, "close", sessionId).catch(err => {
        console.error("Failed to track campaign close event:", err);
      });

      const timer = timersRef.current.get(campaignId);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(campaignId);
      }

      // Functional update: only ever touches this one scope's slice, so
      // closing a campaign in one scope can never race with or clobber
      // another scope's state.
      setScopes(prev => {
        const current = prev[scope];
        if (!current) return prev;

        const remaining = current.active.filter(c => c.id !== campaignId);
        if (remaining.length > 0) {
          // Other campaigns in this scope's priority group are still showing.
          return { ...prev, [scope]: { ...current, active: remaining } };
        }

        // This scope's whole priority group has been closed — advance to
        // its next one, if any.
        if (current.remaining.length > 0) {
          return { ...prev, [scope]: { active: current.remaining[0], remaining: current.remaining.slice(1) } };
        }

        return { ...prev, [scope]: { active: [], remaining: [] } };
      });
    },
    [sessionId]
  );

  const trackClick = useCallback(
    (campaignId: number) => {
      // Track click event in DB
      trackCampaignEvent(campaignId, "click", sessionId).catch(err => {
        console.error("Failed to track campaign click event:", err);
      });
    },
    [sessionId]
  );

  return (
    <CampaignContext.Provider value={{ activeCampaigns, zoneCampaigns, closeCampaign, trackClick, sessionId }}>
      {children}
      {hasHadActiveCampaign && <CampaignAnimatedRenderer campaigns={activeCampaigns} />}
    </CampaignContext.Provider>
  );
};
